// @ts-check
import { graphql } from 'gatsby';
import React from 'react';
import Table from '~/components/Modules/Table';

import './DocumentsNeeded.scss';

export const query = graphql`
	fragment DocumentsNeededFragment on Ozk_pageContent {
		documents_needed {
			table_heading
		}
	}
`;


/**
 * @typedef DocumentsNeededOptions
 * @property {string} table_heading
 *
 * @typedef DocumentsNeededProps
 * @property {DocumentsNeededOptions} options
 **/
/** @type {import('~/types/React').ReactFC<DocumentsNeededProps>} */
const DocumentsNeeded = ({ options }) => (
	<div className="cs-overview container">
		<div className="table-block">
			<h2>{options.table_heading}</h2>

			<Table>
				<DocumentsNeededDesktop />
				<DocumentsNeededMobile />
			</Table>
			<p className="disclosure">*Additional documentation may be required depending on business ownership structure.</p>
		</div>
	</div>
);

const checkMark = String.fromCharCode(10003);
const DocumentsNeededDesktop = () => (
	<table className="documents-needed-desktop-table">
		<thead>
			<tr>
				<th className="heading" scope="col">
					Documents
				</th>
				<th className="heading" scope="col">
					General Partnership
				</th>
				<th className="heading" scope="col">
					Limited Partnership
				</th>
				<th className="heading" scope="col">
					Limited Liability Partnership
				</th>
				<th className="heading" scope="col">
					Limited Liability Company
				</th>
				<th className="heading" scope="col">
					Corporation for Profit
				</th>
				<th className="heading" scope="col">
					Corporation Not for Profit
				</th>
			</tr>
		</thead>
		<tbody>
			<tr>
				<th scope="row">Fictitious Name Certificate, for Multi-Tiered/DBA</th>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td />
			</tr>
			<tr>
				<th scope="row">Business License</th>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
			</tr>
			<tr>
				<th scope="row">Partnership Agreement</th>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td />
				<td />
				<td />
			</tr>
			<tr>
				<th scope="row">
					Certificate of Limited Partnership or Limited Partnership Agreement, registered with the Secretary of State
				</th>
				<td />
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td />
				<td />
				<td />
			</tr>
			<tr>
				<th scope="row">Articles of Incorporation or Certification of Formation</th>
				<td />
				<td />
				<td />
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
			</tr>
			<tr>
				<th scope="row">Operating Agreement/Bylaws</th>
				<td />
				<td />
				<td />
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
			</tr>
			<tr>
				<th scope="row">IRS 501(C) Tax Determination Letter</th>
				<td />
				<td />
				<td />
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
			</tr>
			<tr>
				<th scope="row">
					Certificate of Authority to do Business (if business has physical address or location in state where not
					registered)
				</th>
				<td />
				<td />
				<td />
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
			</tr>
			<tr>
				<th scope="row">ID Image (for beneficial owners, control person, and at a minimum one signer)</th>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
				<td className="item-required">{checkMark}</td>
			</tr>
		</tbody>
	</table>
);

const DocumentsNeededMobile = () => (
	<div className="documents-needed-mobile-table">
		<ul>
			<li>
				<h4>General Partnership</h4>
			</li>
			<li>Fictitious Name Certificate, for Multi-Tiered/DBA</li>
			<li>Business License</li>
			<li>Partnership Agreement</li>
			<li>ID Image (for beneficial owners, control person, and at a minimum one signer)</li>
		</ul>
		<ul>
			<li>
				<h4>Limited Partnership</h4>
			</li>
			<li>Fictitious Name Certificate, for Multi-Tiered/DBA</li>
			<li>Business License</li>
			<li>Partnership Agreement</li>
			<li>
				Certificate of Limited Partnership or Limited Partnership Agreement, registered with the Secretary of State
			</li>
			<li>ID Image (for beneficial owners, control person, and at a minimum one signer)</li>
		</ul>
		<ul>
			<li>
				<h4>Limited Liability Partnership</h4>
			</li>
			<li>Fictitious Name Certificate, for Multi-Tiered/DBA</li>
			<li>Business License</li>
			<li>Partnership Agreement</li>
			<li>
				Certificate of Limited Partnership or Limited Partnership Agreement, registered with the Secretary of State
			</li>
			<li>ID Image (for beneficial owners, control person, and at a minimum one signer)</li>
		</ul>
		<ul>
			<li>
				<h4>Limited Liability Company</h4>
			</li>
			<li>Fictitious Name Certificate, for Multi-Tiered/DBA</li>
			<li>Business License</li>
			<li>Articles of Incorporation or Certification of Formation</li>
			<li>Operating Agreement/Bylaws</li>
			<li>IRS 501(C) Tax Determination Letter</li>
			<li>
				Certificate of Authority to do Business (if business has physical address or location in state where not
				registered)
			</li>
			<li>ID Image (for beneficial owners, control person, and at a minimum one signer)</li>
		</ul>
		<ul>
			<li>
				<h4>Corporation for Profit</h4>
			</li>
			<li>Fictitious Name Certificate, for Multi-Tiered/DBA</li>
			<li>Business License</li>
			<li>Articles of Incorporation or Certification of Formation</li>
			<li>Operating Agreement/Bylaws</li>
			<li>IRS 501(C) Tax Determination Letter</li>
			<li>
				Certificate of Authority to do Business (if business has physical address or location in state where not
				registered)
			</li>
			<li>ID Image (for beneficial owners, control person, and at a minimum one signer)</li>
		</ul>
		<ul>
			<li>
				<h4>Corporation Not for Profit</h4>
			</li>
			<li>Business License</li>
			<li>Articles of Incorporation or Certification of Formation</li>
			<li>Operating Agreement/Bylaws</li>
			<li>IRS 501(C) Tax Determination Letter</li>
			<li>
				Certificate of Authority to do Business (if business has physical address or location in state where not
				registered)
			</li>
			<li>ID Image (for beneficial owners, control person, and at a minimum one signer)</li>
		</ul>
	</div>
);

export default DocumentsNeeded;
